/**
 * Authenticated fetch for customer API.
 * Uses REACT_APP_CUSTOMER_API_BASE_URL.
 * Prefers Super-Admin impersonation token when present.
 */
import { fetchAuthSession } from "aws-amplify/auth";
import { getAuthorizationToken } from "../impersonationSession";

const API_BASE = process.env.REACT_APP_CUSTOMER_API_BASE_URL;

export class CustomerMaintenanceError extends Error {
  constructor(message, details = {}) {
    super(message);
    this.name = "CustomerMaintenanceError";
    this.code = details.code || "CUSTOMER_MAINTENANCE";
    this.ends_at = details.ends_at;
    this.notice_id = details.notice_id;
    this.status = details.status || 503;
  }
}

async function authedRequest(method, path, body, signal) {
  if (!API_BASE) {
    throw new Error("REACT_APP_CUSTOMER_API_BASE_URL is not defined");
  }

  let token = await getAuthorizationToken();
  if (!token) {
    const session = await fetchAuthSession();
    token = session.tokens?.idToken?.toString();
  }

  const resp = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "content-type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    signal,
  });

  const text = await resp.text();

  if (text.startsWith("<!DOCTYPE") || text.startsWith("<html")) {
    throw new Error("Customer API returned HTML (likely auth or routing issue)");
  }

  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error("Invalid JSON returned from customer API");
    }
  }

  if (!resp.ok) {
    if (data?.code === "CUSTOMER_MAINTENANCE") {
      throw new CustomerMaintenanceError(
        data.error || "Customer portal maintenance in progress",
        { ...data, status: resp.status }
      );
    }
    throw new Error(data?.error || `HTTP ${resp.status}`);
  }

  return data;
}

export function authedGet(path, signal) {
  return authedRequest("GET", path, undefined, signal);
}

export function authedPost(path, body, signal) {
  return authedRequest("POST", path, body ?? {}, signal);
}

export function authedPut(path, body, signal) {
  return authedRequest("PUT", path, body ?? {}, signal);
}

export function authedDelete(path, signal) {
  return authedRequest("DELETE", path, undefined, signal);
}
